import React, { useRef, useState } from 'react';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import { authTheme } from '../../styles/authTheme';

interface TwoFactorChallengeProps {
  email?: string;
  onSubmit: (code: string) => Promise<void>;
  onBack?: () => void;
  isSubmitting?: boolean;
  error?: string | null;
}

export function TwoFactorChallenge({
  email,
  onSubmit,
  onBack,
  isSubmitting = false,
  error,
}: TwoFactorChallengeProps) {
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim().replace(/\s/g, '');
    if (!trimmed) return;
    await onSubmit(trimmed);
  };

  return (
    <div className="space-y-5" data-testid="two-factor-challenge">
      <div className="flex flex-col items-center text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-purple-100 text-purple-700">
          <ShieldCheckIcon className="h-6 w-6" aria-hidden />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">Two-factor verification</h2>
        <p className="mt-1 text-sm text-gray-600">
          {email
            ? `Enter the code from your authenticator app for ${email}`
            : 'Enter the code from your authenticator app'}
        </p>
      </div>

      {error ? (
        <div className={authTheme.noticeAlert} role="alert">
          <p>{error}</p>
        </div>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="2fa-challenge-code" className="sr-only">
            {useBackupCode ? 'Backup code' : 'Verification code'}
          </label>
          <input
            ref={inputRef}
            id="2fa-challenge-code"
            type="text"
            inputMode={useBackupCode ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            autoFocus
            maxLength={useBackupCode ? 8 : 6}
            value={code}
            onChange={(e) =>
              setCode(
                useBackupCode
                  ? e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
                  : e.target.value.replace(/\D/g, '').slice(0, 6),
              )
            }
            placeholder={useBackupCode ? 'BACKUPCD' : '000000'}
            className="block w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-center font-mono text-2xl tracking-[0.4em] shadow-sm focus:border-purple-500 focus:ring-purple-500"
          />
        </div>

        <button
          type="button"
          onClick={() => {
            setUseBackupCode(!useBackupCode);
            setCode('');
            inputRef.current?.focus();
          }}
          className={`text-sm ${authTheme.link}`}
        >
          {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
        </button>

        <button
          type="submit"
          disabled={
            isSubmitting ||
            (useBackupCode ? code.length < 8 : code.length < 6)
          }
          className="flex w-full items-center justify-center rounded-xl bg-purple-600 px-4 py-3 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
        >
          {isSubmitting ? 'Verifying…' : 'Verify and continue'}
        </button>

        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
          >
            Back to sign in
          </button>
        ) : null}
      </form>
    </div>
  );
}
